import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface MotionContextType {
  tier: 0 | 1 | 2; // 0=minimal, 1=standard, 2=full
  enableGL: boolean;
  fps: number;
}

const MotionContext = createContext<MotionContextType>({
  tier: 2,
  enableGL: true,
  fps: 60
});

export const MotionOrchestrator = ({ children }: { children: ReactNode }) => {
  const [tier, setTier] = useState<0 | 1 | 2>(2);
  const [enableGL, setEnableGL] = useState(true);
  const [fps, setFps] = useState(60);

  useEffect(() => {
    // Check prefers-reduced-motion
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setTier(0);
      setEnableGL(false);
      return;
    }

    // FPS monitoring
    let frameCount = 0;
    let lastTime = performance.now();
    let lowFpsCount = 0;

    const measureFPS = () => {
      frameCount++;
      const currentTime = performance.now();
      
      if (currentTime >= lastTime + 1000) {
        const currentFPS = Math.round((frameCount * 1000) / (currentTime - lastTime));
        setFps(currentFPS);
        
        // Downgrade if FPS consistently low
        if (currentFPS < 45) {
          lowFpsCount++;
          if (lowFpsCount > 3) {
            setTier(1);
            setEnableGL(false);
          }
        } else {
          lowFpsCount = 0;
        }
        
        frameCount = 0;
        lastTime = currentTime;
      }
      
      requestAnimationFrame(measureFPS);
    };

    const rafId = requestAnimationFrame(measureFPS);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <MotionContext.Provider value={{ tier, enableGL, fps }}>
      {children}
    </MotionContext.Provider>
  );
};

export const useMotion = () => useContext(MotionContext);
